import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { ChakraProvider, Select, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, useDisclosure, Box, Tooltip } from '@chakra-ui/react';
import { ScentDial } from './components';
import emailjs from 'emailjs-com';

function App() {
  const containerRef = useRef(null);
  const [currentModel, setCurrentModel] = useState('/STAR.stl');
  const [currentTexture, setCurrentTexture] = useState('pink');
  const floatingImagesRef = useRef([]);
  const [quantity, setQuantity] = useState(10);
  const intervalRef = useRef(null);
  let updateInterval = false;
  const [scents, setScents] = useState({});
  const [email, setEmail] = useState('');
  const [comments, setComments] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [emailError, setEmailError] = useState(false);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setQuantity(value);
  };

  const handleScentChange = (scent, isChecked, value) => {
    setScents((prevScents) => ({
      ...prevScents,
      [scent]: isChecked ? value : 0,
    }));
  };

  const sendEmail = () => {
    if (email.trim() === '') {
      setEmailError(true);
      return;
    }
    const selectedScents = Object.entries(scents)
      .filter(([scent, value]) => value > 0)
      .map(([scent, value]) => `${scent}: ${value}%`)
      .join(', ');

    const templateParams = {
      shape: currentModel,
      color: currentTexture,
      quantity: quantity,
      scent: selectedScents,
      email: email,
      comments: comments,
    };

    emailjs.send('service_0eovtzw', 'template_5xlfsv4', templateParams, 'JQHWi3cWAVLEBu5V6')
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        onOpen(); // Open the modal on success
      }, (error) => {
        console.log('FAILED...', error);
      });
  };

  useEffect(() => {
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true });

    const resizeRenderer = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        renderer.setSize(clientWidth, clientHeight);
        camera.aspect = clientWidth / clientHeight;
        camera.updateProjectionMatrix();
      }
    };

    resizeRenderer();
    window.addEventListener('resize', resizeRenderer);

    renderer.setClearColor(0x000000, 0);

    if (containerRef.current) {
      containerRef.current.appendChild(renderer.domElement);
    } else {
      console.error('Container element not found');
    }

    const loader = new STLLoader();
    let mesh;

    loader.load(currentModel, (geometry) => {
      geometry.center();
      const boundingBox = new THREE.Box3().setFromObject(new THREE.Mesh(geometry));
      const size = boundingBox.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      let scaleFactor = (1 / maxDim) * 1;

      if (currentModel === '/grip.stl') {
        scaleFactor *= 0.9; // Scale it 90%
      }

      if (currentModel === '/shell01.stl') {
        geometry.rotateX(Math.PI / -2); // Rotate the model by 90 degrees
        geometry.rotateY(Math.PI / 3); // Rotate the model by 90 degrees
      }

      geometry.scale(scaleFactor, scaleFactor, scaleFactor);

      let material;
      switch (currentTexture) {
        case 'transparent':
          material = new THREE.MeshPhongMaterial({ color: 0x808080, transparent: true, opacity: 0.75 });
          break;
        case 'pink':
          const texture = new THREE.TextureLoader().load('/pink and white texture.png');
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(1, 1);
          material = new THREE.MeshPhongMaterial({ map: texture, transparent: false });
          break;
        case 'green':
          material = new THREE.MeshPhongMaterial({ color: 0x013220 });
          break;
        case 'blue':
          material = new THREE.MeshPhongMaterial({ color: 0x015482 });
          break;
        case 'yellow':
          material = new THREE.MeshPhongMaterial({ color: 0xFFFF80 });
          break;
        default:
          material = new THREE.MeshPhongMaterial({ color: 0x808080, transparent: true, opacity: 0.75 });
      }
      mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const lights = [
        new THREE.DirectionalLight(0xffffff, 2),
        new THREE.DirectionalLight(0xffffff, 2),
        new THREE.DirectionalLight(0xffffff, 2),
        new THREE.DirectionalLight(0xffffff, 2)
      ];
      lights[0].position.set(-3, 1, -3);
      lights[1].position.set(3, -1, 3);
      lights[2].position.set(1, 1, -3);
      lights[3].position.set(-1, -1, 3);
      lights.forEach(light => scene.add(light));
      camera.position.z = 1;

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableZoom = false;
      controls.update();

      const animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        mesh.rotation.y += 0.01;
        renderer.render(scene, camera);
      };
      animate();
    });

    return () => {
      while (scene.children.length > 0) {
        scene.remove(scene.children[0]);
      }
      if (containerRef.current) {
        containerRef.current.removeChild(renderer.domElement);
      }
      window.removeEventListener('resize', resizeRenderer);
    };
  }, [currentModel, currentTexture]);

  useEffect(() => {
    const moveImages = () => {
      floatingImagesRef.current.forEach((img) => {
        if (updateInterval == false) {
          const x = parseFloat(img.style.left) + parseFloat(img.dataset.vx);
          const y = parseFloat(img.style.top) + parseFloat(img.dataset.vy);
          console.log(img.style.left)
          if (x <= 0 || x >= window.innerWidth - img.width) {
            img.dataset.vx = -parseFloat(img.dataset.vx);
          }
          if (y <= 0 || y >= window.innerHeight - img.height) {
            img.dataset.vy = -parseFloat(img.dataset.vy);
          }
          img.style.left = `${x}px`;
          img.style.top = `${y}px`;
        }
      });
    };
    if (updateInterval == false) {
      intervalRef.current = setInterval(moveImages, 50);
      updateInterval = true;
    }
  }, []);

  const addFloatingImage = (img) => {
    if (img) {
      img.style.left = `${Math.random() * (window.innerWidth - img.width)}px`;
      img.style.top = `${Math.random() * (window.innerHeight - img.height)}px`;
      img.dataset.vx = `${1.5}`;
      img.dataset.vy = `${1.5}`;
      floatingImagesRef.current.push(img);
    }
  };

  return (
    <ChakraProvider>
      <div className="App">
        <header className="App-header">
          <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '15px' }}>
            <h1 style={{ marginRight: '5px' }}><b><a href="https://slugsoap.net" style={{ textDecoration: 'underline' }}>slug soap</a></b><b> custom order</b></h1>
            <img className="small-logo" style={{ width: '6rem', height: 'auto' }} src="/logo.gif" alt="logo" />
          </div>
          <div className="container">
            <div className="left-column">
              <div className="select-container">
                <Select width="8vw" minWidth="100px" onChange={(e) => setCurrentModel(e.target.value)}>
                  <option value="/STAR.stl">star</option>
                  <option value="/grip.stl">grip</option>
                  <option value="/cheese02.stl">cheese</option>
                  <option value="/FLOWER S.stl">flower</option>
                  <option value="/shell01.stl">shell</option>
                </Select>
                <p background-color='white'>in</p>
                <Select width="8vw" minWidth="100px" onChange={(e) => setCurrentTexture(e.target.value)}>
                  <option value="pink">pink</option>
                  <option value="transparent">clear</option>
                  <option value="yellow">yellow</option>
                  <option value="blue">blue</option>
                  <option value="green">green</option>
                </Select>
              </div>
              <br />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <ScentDial scent="eucalyptus" onChange={handleScentChange} />
                <br />
                <ScentDial scent="petrichor (rain)" onChange={handleScentChange} />
                <br />
                <ScentDial scent="laundry (fresh linen)" onChange={handleScentChange} />
                <br />
                <ScentDial scent="bergamot (citrus)" onChange={handleScentChange} />
                <br />
                <div className="input-item" style={{ margin: '7px', width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                  <i>order quantity: </i>
                  <input
                    type="number"
                    value={quantity}
                    onChange={handleQuantityChange}
                    min="1"
                    className="input-box"
                    style={{ padding: '10px', marginBottom: '10px', border: '1px solid grey', backgroundColor: 'transparent' }}
                  />
                </div>
                <div className="input-item" style={{ margin: '7px', width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                  <i>email: </i>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(false); // Reset error on change
                    }}
                    onBlur={() => {
                      if (email.trim() === '') {
                        setEmailError(true);
                      }
                    }}
                    className={`input-box ${emailError ? 'input-error' : ''}`}
                    style={{ padding: '10px', marginBottom: '10px', backgroundColor: 'transparent', border: '1px solid grey' }}
                  />
                </div>
                <div className="input-item" style={{ margin: '7px', width: '100%' }}>
                  <i style={{ textAlign: 'left', display: 'block' }}>comments:</i>
                  <textarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    className="input-box"
                    style={{ padding: '10px', marginBottom: '10px', border: '1px solid grey', backgroundColor: 'transparent', width: '100%' }}
                  />
                </div>
              </div>
            </div>
            <div className="right-column">
              <div ref={containerRef} id="modelContainer" ></div>
              <br />
            </div>
          </div>
          <br />
          {(quantity < 10 || isNaN(quantity)) && (
            <p style={{ color: 'red', fontSize: '18px' }}>
              you must order more than 10 bars!
            </p>
          )}
          <Tooltip 
            label="add an email first!" 
            isDisabled={email.trim() !== ''}
            className='tooltip-custom'
          >
            <button
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                backgroundColor: 'grey',
                color: 'white',
                border: '2px dashed',
                textDecoration: (quantity < 10 || isNaN(quantity) || email.trim() === '') ? 'line-through' : 'none',
                cursor: (quantity < 10 || isNaN(quantity) || email.trim() === '') ? 'not-allowed' : 'pointer'
              }}
              disabled={quantity < 10 || isNaN(quantity) || email.trim() === ''}
              onClick={sendEmail}
            >
              request a quote
            </button>
          </Tooltip>
        </header>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalBody>
              <Box textAlign="center" fontFamily="monospace">
                Thank you for your request!
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
        <div className="floating-images">
          <img src="/spiral1.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/spiral2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/star1 2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/spiral1.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/spiral2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/star1 2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/star2 2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
          <img src="/star2 2.png" alt="floating" className="floating-image" ref={addFloatingImage} />
        </div>
      </div>
    </ChakraProvider>
  );
}

export default App;